import type { ComponentType, FC } from 'react';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import Badge from '@atlaskit/badge';
import FlagFilledIcon from '@atlaskit/icon/core/flag-filled';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import type { LocalOverrides } from '@atlaskit/feature-gate-js-client';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';

import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import type { DialogsContainerProps } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { DevToolsDialog } from '@confluence/dev-tools';

import { ConfigurationButton } from './ConfigurationButton.tsx';

const buttonAndBadgeStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
	marginRight: 'space.negative.150',
});
const badgeNudgerStyles = xcss({
	position: 'relative',
	bottom: '10px',
	right: '10px',
	width: 'max-content',
});

const tooltipListStyles = xcss({
	paddingLeft: 'space.200',
	marginBottom: 'space.100',
});

const i18n = defineMessages({
	statsigOverrides: {
		id: 'app-navigation.feature-flag-logo.statsig-overrides',
		defaultMessage: 'Statsig overrides:',
		description: 'Label for the top navigation feature flag logo tooltip',
	},
});

export const FeatureFlagLogo: FC<{
	featureGateOverrides: LocalOverrides;
}> = ({ featureGateOverrides }) => {
	const { showModal } = useDialogs();

	const hide = confluenceLocalStorageInstance.getItemAsBoolean(keys.HIDE_FEATURE_FLAG_ICON);
	if (hide) return null;

	return (
		<Box xcss={buttonAndBadgeStyles}>
			<Tooltip
				content={
					<>
						<Text as="p" color="color.text.inverse">
							<FormattedMessage {...i18n.statsigOverrides} />
						</Text>
						<Stack as="ul" xcss={tooltipListStyles}>
							{Object.entries(featureGateOverrides.gates).map(([name, value]) => (
								<li key={name}>
									{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
									{name} ({JSON.stringify(value)})
								</li>
							))}
							{Object.entries(featureGateOverrides.configs).map(([name, value]) => (
								<li key={name}>
									{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
									{name} ({JSON.stringify(value)})
								</li>
							))}
						</Stack>
					</>
				}
			>
				<ConfigurationButton
					onClick={() => {
						showModal(DevToolsDialog as ComponentType<DialogsContainerProps>, {});
					}}
				>
					<FlagFilledIcon label="Statsig overrides" />
				</ConfigurationButton>
			</Tooltip>
			<Box xcss={badgeNudgerStyles}>
				<Badge appearance="important">
					{Object.keys(featureGateOverrides.gates).length +
						Object.keys(featureGateOverrides.configs).length}
				</Badge>
			</Box>
		</Box>
	);
};
